import { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import {
  DEMO_MELODIES,
  createWebSessionResultStore,
  native,
  scoreFrame,
  summarizeSession,
} from '@muzly/shared';
import type {
  FrameScore,
  ScoredFrame,
  SessionRecord,
  SessionResult,
  TargetMelody,
} from '@muzly/shared';

import { createGuideTonePlayer } from './guideTone';
import { InfoTip } from './InfoTip';
import { LiveScoreView } from './LiveScoreView';
import { MelodyPreview } from './MelodyPreview';
import { INITIAL_PRACTICE_STATE, practiceFlowReducer } from './practiceFlow';
import { downloadSessionResult } from './scoreExport';
import { ScoreOverlay } from './ScoreOverlay';
import { formatPercent } from './scoringView';
import { useSessionControl } from './sessionControlContext';

import type { GuideTonePlayer } from './guideTone';
import type { OverlayPoint } from './scoreOverlayGeometry';

/**
 * 콘솔 스코어링 패널(#5·#8·#9) — 데모 멜로디를 골라 **연습 플로우**를 돌린다:
 * 준비 → 3·2·1 카운트다운 → 연습중(라이브 뷰) → 멜로디 끝나면 자동 정지 → 결과 카드.
 * 결과에서 다시하기/멜로디 변경. 연습중 "중단"으로 준비로 되돌릴 수 있다.
 *
 * 단계 전이는 순수 머신(`practiceFlow`)이 소유하고, 이 컴포넌트는 카운트다운 틱·세션
 * start/stop·프레임 구독 같은 부수효과만 얹는다. 비교/요약은 순수 코어(`@muzly/shared`
 * scoring) — raw f0만 쓰고 표시 스무딩 값은 점수에 넣지 않는다. 캡처→곡 타임라인 정렬은
 * 연습중 첫 프레임 timestampMs를 기준점(anchor)으로 잡아 곡 0ms에 맞춘다. 네이티브가
 * 없으면 mock 스트림으로 같은 흐름이 브라우저에서 동작한다. dev 전용 패널.
 */

/** 멜로디 마지막 노트가 끝나는 곡 시각(ms) — 연습 자동 정지 기준. */
const melodyEndMs = (melody: TargetMelody): number =>
  melody.notes.reduce((end, n) => Math.max(end, n.startMs + n.durMs), 0);

// 결과 저장소는 모듈 1회 생성(localStorage 백엔드, 없으면 in-memory 폴백).
const sessionStore = createWebSessionResultStore();

interface LiveScore {
  score: FrameScore;
  atMs: number;
}

const MelodyPicker = ({
  melody,
  onChange,
  disabled,
}: {
  melody: TargetMelody;
  onChange: (id: string) => void;
  disabled: boolean;
}) => (
  <label className="flex items-center gap-2 text-xs text-muted-foreground">
    <span className="flex items-center gap-1">
      목표 멜로디
      <InfoTip label="목표 멜로디 설명">
        부를 목표 음 시퀀스입니다. 세션을 시작하면 이 멜로디의 음과 실제
        음높이를 비교합니다. 시작 후에는 정지해야 바꿀 수 있습니다.
      </InfoTip>
    </span>
    <select
      value={melody.id}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[40px] flex-1 rounded-md border border-border bg-card px-2 py-1 font-sans text-sm text-foreground transition disabled:cursor-not-allowed disabled:opacity-50"
    >
      {DEMO_MELODIES.map((m) => (
        <option key={m.id} value={m.id}>
          {m.title} · {m.notes.length}음
        </option>
      ))}
    </select>
  </label>
);

const Guidance = () => (
  <p className="rounded-md bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
    멜로디를 고르고 세션을 시작하면 목표 음 대비 실시간 판정이 여기 표시됩니다.
  </p>
);

const Stat = ({
  label,
  value,
  tip,
}: {
  label: string;
  value: string;
  tip: string;
}) => (
  <div className="flex flex-col gap-0.5 rounded-md border border-border bg-card p-2">
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      {label}
      <InfoTip label={`${label} 설명`}>{tip}</InfoTip>
    </span>
    <span className="font-mono text-sm text-foreground">{value}</span>
  </div>
);

const ResultCard = ({ result }: { result: SessionResult }) => {
  const hits = result.perNote.filter((n) => n.verdict === 'hit').length;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/[0.02] p-3">
      <div className="grid grid-cols-2 gap-2">
        <Stat
          label="정확도"
          value={formatPercent(result.accuracyPct)}
          tip="판정된 프레임 중 음정이 맞은(in-tune) 비율입니다. 놓친 구간은 분모에 포함됩니다."
        />
        <Stat
          label="평균 cents"
          value={
            result.meanAbsCents === null
              ? '—'
              : `${result.meanAbsCents.toFixed(0)}¢`
          }
          tip="목표 음과 얼마나 떨어졌는지의 평균(절댓값)입니다. 0에 가까울수록 정확합니다."
        />
        <Stat
          label="in-tune 비율"
          value={formatPercent(result.inTuneRatio * 100)}
          tip="소리를 낸 프레임 중 정확했던 비율입니다(놓침 제외)."
        />
        <Stat
          label="음표 적중"
          value={`${hits} / ${result.perNote.length}`}
          tip="각 음을 충분히 맞춘 음표 수입니다."
        />
      </div>

      <div>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          약점 구간
          <InfoTip label="약점 구간 설명">
            연속으로 음이 흔들리거나 놓친 시간 구간입니다(곡 기준 초). 끊김
            구간은 과도 반영되지 않게 제외합니다.
          </InfoTip>
        </span>
        {result.unstableSections.length === 0 ? (
          <p className="mt-1 text-xs text-emerald-600">뚜렷한 약점 구간 없음</p>
        ) : (
          <ul className="mt-1 flex flex-wrap gap-1">
            {result.unstableSections.map((s) => (
              <li
                key={`${s.startMs}-${s.endMs}`}
                className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 font-mono text-[11px] text-amber-600"
              >
                {(s.startMs / 1000).toFixed(1)}–{(s.endMs / 1000).toFixed(1)}s
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const RecentList = ({
  records,
  onClear,
}: {
  records: SessionRecord[];
  onClear: () => void;
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        최근 결과
        <InfoTip label="최근 결과 설명">
          정지할 때마다 저장된 연습 결과입니다(이 기기에만 보관, 음성은 저장하지
          않음). 최신 항목이 위에 옵니다.
        </InfoTip>
      </span>
      {records.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground transition hover:bg-muted"
        >
          비우기
        </button>
      )}
    </div>
    {records.length === 0 ? (
      <p className="text-xs text-muted-foreground/70">
        아직 저장된 결과가 없습니다.
      </p>
    ) : (
      <ul className="flex flex-col gap-1">
        {records.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between rounded-md border border-border/40 px-2 py-1 text-xs"
          >
            <span className="truncate text-foreground">{r.title}</span>
            <span className="ml-2 shrink-0 font-mono text-muted-foreground">
              {formatPercent(r.result.accuracyPct)}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

/** 가이드 톤 on/off 토글(기본 on) + 준비 단계 미리듣기. */
const GuideToneControls = ({
  guideOn,
  onToggle,
  onPreview,
  showPreview,
}: {
  guideOn: boolean;
  onToggle: (on: boolean) => void;
  onPreview: () => void;
  showPreview: boolean;
}) => (
  <div className="flex items-center gap-2 text-xs text-muted-foreground">
    <label className="flex items-center gap-1.5">
      <input
        type="checkbox"
        checked={guideOn}
        onChange={(e) => onToggle(e.target.checked)}
        className="h-4 w-4 accent-primary"
      />
      <span className="flex items-center gap-1">
        가이드 톤
        <InfoTip label="가이드 톤 설명">
          목표 음을 소리로 들려줘 따라 부르기 쉽게 합니다. 연습을 시작하면 노트
          타이밍대로 재생됩니다(볼륨 낮음). 끄면 소리 없이 진행합니다.
        </InfoTip>
      </span>
    </label>
    {showPreview && (
      <button
        type="button"
        onClick={onPreview}
        className="rounded-full border border-border/60 px-2.5 py-1 text-[11px] text-foreground transition hover:bg-muted"
      >
        미리듣기
      </button>
    )}
  </div>
);

export const ScoringPanel = () => {
  const { sessionState, run } = useSessionControl();
  const [flow, dispatch] = useReducer(
    practiceFlowReducer,
    INITIAL_PRACTICE_STATE,
  );
  const [melodyId, setMelodyId] = useState<string>(DEMO_MELODIES[0].id);
  const [guideOn, setGuideOn] = useState(true);
  const [live, setLive] = useState<LiveScore | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [records, setRecords] = useState<SessionRecord[]>([]);
  // 정지 후 리뷰: 마지막 세션의 저장 레코드(export) + 실제 음높이 곡선(overlay).
  const [lastRecord, setLastRecord] = useState<SessionRecord | null>(null);
  const [overlay, setOverlay] = useState<{
    melody: TargetMelody;
    actual: OverlayPoint[];
  } | null>(null);

  const melody = useMemo(
    () => DEMO_MELODIES.find((m) => m.id === melodyId) ?? DEMO_MELODIES[0],
    [melodyId],
  );

  // 구독 클로저가 항상 최신 값을 보도록 ref로 미러링한다.
  const melodyRef = useRef(melody);
  melodyRef.current = melody;
  const endMsRef = useRef(0);
  endMsRef.current = melodyEndMs(melody);
  const phaseRef = useRef(flow.phase);
  phaseRef.current = flow.phase;
  const runRef = useRef(run);
  runRef.current = run;
  // 곡 타임라인 누적(요약용) + 캡처→곡 기준점(연습중 첫 프레임 timestampMs).
  const framesRef = useRef<ScoredFrame[]>([]);
  const anchorRef = useRef<number | null>(null);
  // 오버레이용 실제 음높이(raw midi) 누적 — 점수와 별개의 비교 시각화.
  const actualRef = useRef<OverlayPoint[]>([]);
  // 멜로디 끝에서 자동 정지를 한 번만 보내기 위한 가드.
  const finishingRef = useRef(false);
  // "중단"으로 멈춘 정지는 결과/저장 없이 흡수하기 위한 플래그.
  const abortingRef = useRef(false);
  // 가이드 톤 재생기(지연 생성) + 최신 on/off 미러.
  const playerRef = useRef<GuideTonePlayer | null>(null);
  const guideOnRef = useRef(guideOn);
  guideOnRef.current = guideOn;

  const getPlayer = (): GuideTonePlayer => {
    if (!playerRef.current) {
      playerRef.current = createGuideTonePlayer();
    }
    return playerRef.current;
  };

  // 멜로디를 바꾸면 직전 누적은 다른 목표 기준이라 폐기하고 준비 단계로 되돌린다.
  useEffect(() => {
    framesRef.current = [];
    anchorRef.current = null;
    actualRef.current = [];
    finishingRef.current = false;
    setLive(null);
    setResult(null);
    setOverlay(null);
    setLastRecord(null);
    dispatch({ type: 'reset' });
  }, [melodyId]);

  // 최초 진입 시 저장된 최근 결과를 불러온다.
  useEffect(() => {
    sessionStore
      .list()
      .then(setRecords)
      .catch(() => undefined);
  }, []);

  // 카운트다운: 1초마다 틱(3→2→1→연습중). 시작은 버튼이 세션을 켰다.
  useEffect(() => {
    if (flow.phase !== 'countdown') {
      return;
    }
    const id = window.setInterval(() => dispatch({ type: 'tick' }), 1000);
    return () => window.clearInterval(id);
  }, [flow.phase]);

  // 연습중 진입: 누적을 리셋해 곡 0ms부터 새로 채운다(카운트다운 동안의 프레임 폐기).
  // 세션이 (어떤 이유로) 아직 idle이면 여기서 보강 시작한다.
  useEffect(() => {
    if (flow.phase !== 'practicing') {
      return;
    }
    framesRef.current = [];
    actualRef.current = [];
    anchorRef.current = null;
    finishingRef.current = false;
    setResult(null);
    setOverlay(null);
    if (sessionState === 'idle') {
      run('start');
    }
    // sessionState/run은 의도적으로 deps 제외 — 단계 진입 시 1회만 실행.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.phase]);

  // 가이드 톤: 연습중 진입 시(곡 0ms) 노트 타이밍대로 재생, 나갈 때 정지.
  // guideOn은 진입 시점 값으로 결정 — 중간 토글은 아래 정지 효과가 처리한다.
  useEffect(() => {
    if (flow.phase !== 'practicing') {
      return;
    }
    const player = getPlayer();
    if (guideOnRef.current) {
      player.play(melodyRef.current);
    }
    return () => player.stop();
  }, [flow.phase]);

  // 연습 중 가이드를 끄면 즉시 정지(다시 켜도 재시작하지 않아 곡과 어긋나지 않게).
  useEffect(() => {
    if (!guideOn) {
      playerRef.current?.stop();
    }
  }, [guideOn]);

  // 언마운트 시 AudioContext 해제.
  useEffect(() => () => playerRef.current?.dispose(), []);

  // pitchTrackerEvent 구독(마운트 1회): 연습중에만 판정·누적, 멜로디 끝나면 자동 정지.
  useEffect(
    () =>
      native.on('pitchTrackerEvent', (event) => {
        if (event.type === 'pitch_frame') {
          if (phaseRef.current !== 'practicing') {
            return;
          }
          const frame = event.frame;
          if (!frame) {
            return;
          }
          if (anchorRef.current === null) {
            anchorRef.current = frame.timestampMs;
          }
          const atMs = frame.timestampMs - anchorRef.current;
          const score = scoreFrame(frame, melodyRef.current, atMs);
          framesRef.current.push({
            atMs,
            score,
            discontinuity: frame.continuity?.discontinuity,
          });
          actualRef.current.push({
            atMs,
            midi: frame.voiced ? frame.midi : null,
            confidence: frame.confidence,
          });
          setLive({ score, atMs });
          // 멜로디 마지막 노트가 끝나면 자동 정지(한 번만) → session_stopped에서 결과.
          if (!finishingRef.current && atMs >= endMsRef.current) {
            finishingRef.current = true;
            runRef.current('stop');
          }
          return;
        }
        if (event.type === 'session_stopped') {
          const aborting = abortingRef.current;
          const frames = framesRef.current;
          const actual = actualRef.current;
          framesRef.current = [];
          actualRef.current = [];
          anchorRef.current = null;
          finishingRef.current = false;
          abortingRef.current = false;
          setLive(null);
          if (aborting) {
            return; // 중단 — 결과/저장 없이 흡수(준비로는 abort 디스패치가 보냄).
          }
          if (frames.length === 0) {
            dispatch({ type: 'abort' });
            return;
          }
          const m = melodyRef.current;
          const summary = summarizeSession(frames, m);
          setResult(summary);
          setOverlay({ melody: m, actual });
          const now = Date.now();
          const record: SessionRecord = {
            id: `${m.id}-${now}`,
            savedAtMs: now,
            melodyId: m.id,
            title: m.title,
            result: summary,
          };
          setLastRecord(record);
          sessionStore
            .save(record)
            .then(() => sessionStore.list())
            .then(setRecords)
            .catch(() => undefined);
          dispatch({ type: 'finish' });
          return;
        }
        if (event.type === 'error') {
          framesRef.current = [];
          actualRef.current = [];
          anchorRef.current = null;
          finishingRef.current = false;
          abortingRef.current = false;
          setLive(null);
          dispatch({ type: 'abort' });
        }
      }),
    [],
  );

  const clearRecords = (): void => {
    sessionStore
      .clear()
      .then(() => setRecords([]))
      .catch(() => undefined);
  };

  // "연습 시작" — 카운트다운을 열고, 세션이 idle이면 카운트다운 중 미리 켠다.
  const startPractice = (): void => {
    setResult(null);
    setOverlay(null);
    if (sessionState === 'idle') {
      run('start');
    }
    dispatch({ type: 'start' });
  };

  // "중단" — 진행 중 세션을 멈추고(결과 없이) 준비로 되돌린다.
  const abortPractice = (): void => {
    abortingRef.current = true;
    if (sessionState !== 'idle') {
      run('stop');
    }
    dispatch({ type: 'abort' });
  };

  // "미리듣기" — 준비 단계에서 목표 멜로디를 한 번 들려준다(토글과 무관, 명시적 재생).
  const previewGuide = (): void => {
    getPlayer().play(melody);
  };

  const isReady = flow.phase === 'ready';
  const isCountdown = flow.phase === 'countdown';

  return (
    <div className="flex flex-col gap-3">
      <MelodyPicker
        melody={melody}
        onChange={setMelodyId}
        disabled={!isReady}
      />
      <GuideToneControls
        guideOn={guideOn}
        onToggle={setGuideOn}
        onPreview={previewGuide}
        showPreview={isReady}
      />

      {(isReady || isCountdown) && (
        <>
          <MelodyPreview
            melody={melody}
            countdown={isCountdown ? flow.count : null}
            highlightFirstNote={isCountdown && flow.count === 1}
          />
          {isReady ? (
            <>
              <Guidance />
              <button
                type="button"
                onClick={startPractice}
                className="self-start rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 motion-safe:active:scale-95"
              >
                연습 시작
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={abortPractice}
              className="self-start rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground transition hover:bg-muted"
            >
              중단
            </button>
          )}
        </>
      )}

      {flow.phase === 'practicing' && live && (
        <>
          <LiveScoreView
            melody={melody}
            atMs={live.atMs}
            score={live.score}
            frames={framesRef.current}
            actual={actualRef.current}
          />
          <button
            type="button"
            onClick={abortPractice}
            className="self-start rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground transition hover:bg-muted"
          >
            중단
          </button>
        </>
      )}

      {flow.phase === 'result' && result && (
        <>
          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
            결과 저장됨
          </span>
          <ResultCard result={result} />
          {overlay && overlay.actual.length > 0 && (
            <ScoreOverlay melody={overlay.melody} actual={overlay.actual} />
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => dispatch({ type: 'retry' })}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 motion-safe:active:scale-95"
            >
              다시하기
            </button>
            <button
              type="button"
              onClick={() => dispatch({ type: 'reset' })}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
            >
              멜로디 변경
            </button>
            {lastRecord && (
              <button
                type="button"
                onClick={() => downloadSessionResult(lastRecord)}
                className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition hover:bg-muted"
              >
                결과 JSON 내보내기
              </button>
            )}
          </div>
        </>
      )}

      <RecentList records={records} onClear={clearRecords} />
    </div>
  );
};
